/**
 * main-product-v2.js
 *
 * Self-contained controller for sections/main-product-v2.liquid.
 *
 * Responsibilities:
 *  - Variant resolution for both layouts:
 *      Layout 1 "boxes": option radios (Pack Size / Flavor / ...)
 *      Layout 2 "cards": one radio per variant (Pack Cards)
 *  - Price / compare price / discount badge / unit price updates
 *    (all money strings are pre-formatted by Liquid — no client-side
 *    currency formatting, so Markets / multi-currency keep working)
 *  - Gallery updates via the theme's native <media-gallery> component
 *  - Pack quantity logic: submitted quantity = visible quantity x the
 *    selected variant's custom.pack_quantity metafield (default 1).
 *    The hidden [name="quantity"] input inside the product form is the
 *    single source of truth, so the theme's AJAX <product-form> and
 *    Shopify's dynamic checkout (Buy Now) both respect it.
 *  - Publishes the theme's PUB_SUB_EVENTS.variantChange event when the
 *    pub/sub helpers are available, so cart drawer / app scripts that
 *    listen for variant changes keep working.
 *
 * No jQuery. No external dependencies.
 */

if (!customElements.get('product-info-v2')) {
  customElements.define(
    'product-info-v2',
    class ProductInfoV2 extends HTMLElement {
      connectedCallback() {
        this.sectionId = this.dataset.section;
        this.productUrl = this.dataset.url;
        this.shouldUpdateUrl = this.dataset.updateUrl !== 'false';

        this.variants = this.readVariantData();

        this.form = this.querySelector(`#product-form-${this.sectionId}`);
        this.variantIdInput = this.querySelector('[data-v2-variant-id]');
        this.effectiveQtyInput = this.querySelector('[data-v2-effective-quantity]');
        this.qtyInput = this.querySelector('[data-v2-qty-input]');
        this.submitButton = this.querySelector(`#ProductSubmitButton-${this.sectionId}`);
        this.submitButtonText = this.querySelector('[data-v2-atc-text]');
        this.buyButtons = this.querySelector('[data-v2-buy-buttons]');

        this.priceEl = this.querySelector('[data-v2-price]');
        this.compareEl = this.querySelector('[data-v2-compare]');
        this.discountBadgeEl = this.querySelector('[data-v2-discount-badge]');
        this.perGramEl = this.querySelector('[data-v2-per-gram]');

        this.addEventListener('change', this.onChange.bind(this));
        this.addEventListener('click', this.onClick.bind(this));

        this.initWishlist();

        // Initial sync (covers ?variant= deep links and quantity rules).
        // For the metaobject pack layout: the product's Default Pack Quantity
        // metafield always drives the quantity box on load (whether or not a
        // pack matches). When a pack IS matched, Liquid has already checked it
        // and syncQuantityToCheckedPack keeps box and card consistent.
        this.applyDefaultPackQuantity();
        this.syncQuantityToCheckedPack();
        this.updateEffectiveQuantity();
        this.updateOptionStates();
        this.syncPackMetaDisplay();
      }

      /* ------------------------------------------------------------------ *
       * Data
       * ------------------------------------------------------------------ */

      readVariantData() {
        const dataEl = this.querySelector(`[data-product-v2-variants="${this.dataset.section}"]`) ||
          this.querySelector('[data-product-v2-variants]');
        if (!dataEl) return [];
        try {
          return JSON.parse(dataEl.textContent);
        } catch (error) {
          console.error('[main-product-v2] Could not parse variant data', error);
          return [];
        }
      }

      get variantStyle() {
        return this.dataset.variantStyle || 'boxes';
      }

      get selectedOptionValues() {
        return Array.from(this.querySelectorAll('[data-v2-option]:checked'))
          .sort((a, b) => Number(a.dataset.optionPosition) - Number(b.dataset.optionPosition))
          .map((input) => input.value);
      }

      get currentVariant() {
        if (this.variants.length === 1) return this.variants[0];

        const packInput = this.querySelector('[data-v2-pack]:checked');
        if (packInput) {
          return this.variants.find((variant) => String(variant.id) === packInput.value) || null;
        }

        const selected = this.selectedOptionValues;
        if (!selected.length) return this.variants[0] || null;

        return (
          this.variants.find((variant) =>
            variant.options.every((option, index) => option === selected[index])
          ) || null
        );
      }

      packQuantityFor(variant) {
        // Metaobject-driven pack cards take precedence over the variant's
        // custom.pack_quantity metafield.
        const metaPack = this.checkedPackMeta;
        if (metaPack) {
          const metaQty = parseInt(metaPack.dataset.packQuantity, 10);
          return Number.isFinite(metaQty) && metaQty >= 1 ? metaQty : 1;
        }

        const qty = variant ? parseInt(variant.pack_quantity, 10) : 1;
        return Number.isFinite(qty) && qty >= 1 ? qty : 1;
      }

      get checkedPackMeta() {
        return this.querySelector('[data-v2-pack-meta]:checked');
      }

      get packMetaInputs() {
        return Array.from(this.querySelectorAll('[data-v2-pack-meta]'));
      }

      /* ------------------------------------------------------------------ *
       * Events
       * ------------------------------------------------------------------ */

      onChange(event) {
        const target = event.target;

        if (target.matches('[data-v2-option], [data-v2-pack]')) {
          this.onVariantChange();
          return;
        }

        if (target.matches('[data-v2-pack-meta]')) {
          this.onPackMetaChange();
          return;
        }

        if (target.matches('[data-v2-qty-input]')) {
          this.normalizeQuantityInput();
          this.syncPackToQuantity();
          this.updateEffectiveQuantity();
        }
      }

      onClick(event) {
        const minus = event.target.closest('[data-v2-qty-minus]');
        const plus = event.target.closest('[data-v2-qty-plus]');
        if (!minus && !plus) return;
        if (!this.qtyInput) return;

        event.preventDefault();
        const step = parseInt(this.qtyInput.step, 10) || 1;
        const current = parseInt(this.qtyInput.value, 10) || 1;
        this.qtyInput.value = minus ? current - step : current + step;
        this.normalizeQuantityInput();
        this.syncPackToQuantity();
        this.updateEffectiveQuantity();
      }

      /**
       * Metaobject pack card selected: the variant does not change — only
       * the submitted quantity and the displayed pack pricing do.
       */
      onPackMetaChange() {
        // Selecting a pack makes the visible quantity box match that pack's
        // configured quantity (the box stays the single source of truth for
        // how many units are submitted).
        this.syncQuantityToCheckedPack();
        this.updateEffectiveQuantity();
        this.syncPackMetaDisplay();

        this.dispatchEvent(
          new CustomEvent('product-info-v2:pack-change', {
            bubbles: true,
            detail: {
              sectionId: this.sectionId,
              packQuantity: this.packQuantityFor(this.currentVariant),
            },
          })
        );
      }

      /**
       * Metaobject pack layout only, initial load only. Forces the visible
       * quantity box to the product's Default Pack Quantity metafield value
       * (data-default-pack-qty), regardless of whether any pack matches it.
       * Liquid handles which card (if any) is checked; this only guarantees
       * the box always reflects the configured default. No-op when the
       * metafield is empty/invalid or this is not the metaobject layout, so
       * the existing default quantity behavior is untouched.
       */
      applyDefaultPackQuantity() {
        if (!this.qtyInput) return;
        if (!this.packMetaInputs.length) return; // metaobject pack layout only
        const raw = this.dataset.defaultPackQty;
        if (raw == null || raw === '') return;
        const qty = parseInt(raw, 10);
        if (!Number.isFinite(qty) || qty < 1) return;
        this.qtyInput.value = qty;
        this.normalizeQuantityInput();
      }

      /**
       * Metaobject pack layout only. Copies the currently checked pack card's
       * configured quantity into the visible quantity box. Setting `.value`
       * does not fire a change/input event, so this never re-triggers the
       * quantity handlers (no feedback loop).
       */
      syncQuantityToCheckedPack() {
        const metaPack = this.checkedPackMeta;
        if (!metaPack || !this.qtyInput) return;
        const packQty = parseInt(metaPack.dataset.packQuantity, 10);
        if (!Number.isFinite(packQty) || packQty < 1) return;
        this.qtyInput.value = packQty;
        this.normalizeQuantityInput();
      }

      /**
       * Metaobject pack layout only. After the visible quantity changes (via
       * +/- or manual input), keep the pack cards in sync: select the pack
       * whose quantity matches the box EXACTLY, otherwise deselect every pack.
       * Never rounds or changes the quantity — the box stays the source of
       * truth. Toggling `.checked` programmatically fires no change event, so
       * there is no loop back into onPackMetaChange.
       */
      syncPackToQuantity() {
        const metaInputs = this.packMetaInputs;
        if (!metaInputs.length) return; // not the metaobject pack layout

        const qty = this.qtyInput ? parseInt(this.qtyInput.value, 10) : NaN;

        let matched = null;
        metaInputs.forEach((input) => {
          const packQty = parseInt(input.dataset.packQuantity, 10);
          const isMatch = Number.isFinite(qty) && packQty === qty;
          input.checked = isMatch;
          if (isMatch) matched = input;
        });

        if (matched) {
          // Reflect the newly selected pack's pricing / discount.
          this.syncPackMetaDisplay();
        } else {
          // No exact match: no pack is selected, so restore the base variant
          // price display (updatePrice returns early only when a pack is
          // checked, which is no longer the case here).
          this.updatePrice(this.currentVariant);
        }
      }

      /**
       * Builds the unified savings badge text — "Save ₹ X (Y%)" — from the
       * currently active selling and compare-at money strings (both are
       * pre-formatted money strings such as "₹ 269.00"). The currency symbol
       * is reused from those strings, so nothing is hardcoded. Returns '' when
       * there is no positive saving to show.
       *
       *   Savings Amount     = Compare - Selling
       *   Savings Percentage = round(Amount / Compare * 100)
       */
      savingsBadgeText(sellingStr, compareStr) {
        const selling = this.parseMoneyValue(sellingStr);
        const compare = this.parseMoneyValue(compareStr);
        if (!Number.isFinite(selling) || !Number.isFinite(compare) || compare <= selling) {
          return '';
        }
        const amount = compare - selling;
        const percent = Math.round((amount / compare) * 100);
        // Savings over 60% are always a compare-at data error — suppress the
        // badge and log so the bad price gets noticed (audit K01).
        if (percent > 60) {
          console.warn(
            `[main-product-v2] Save badge suppressed: ${percent}% off (${compareStr} vs ${sellingStr}) looks like a compare-at price data error.`
          );
          return '';
        }
        const symbol = this.currencySymbolOf(compareStr) || this.currencySymbolOf(sellingStr);
        // Drop a trailing ".00" but keep genuine decimals.
        const amountText = String(parseFloat(amount.toFixed(2)));
        return `Save ${symbol}${amountText} (${percent}%)`;
      }

      /** Numeric value of a money string, tolerant of currency symbol and thousands separators. */
      parseMoneyValue(str) {
        if (str == null) return NaN;
        const cleaned = String(str).replace(/[^\d.,]/g, '').replace(/,/g, '');
        return cleaned === '' ? NaN : parseFloat(cleaned);
      }

      /** Leading non-digit portion of a money string, e.g. "₹ " from "₹ 269.00". */
      currencySymbolOf(str) {
        const match = String(str == null ? '' : str).match(/^[^\d]*/);
        return match ? match[0] : '';
      }

      /**
       * Mirrors the checked metaobject pack card into the main price block
       * (price, compare price, discount pill). Money strings come straight
       * from Liquid-rendered data attributes — no client-side formatting.
       */
      syncPackMetaDisplay() {
        const metaPack = this.checkedPackMeta;
        if (!metaPack || !this.priceEl) return;

        this.priceEl.textContent = metaPack.dataset.price;

        if (this.compareEl) {
          const compare = metaPack.dataset.compare;
          if (compare) {
            const srOnly = this.compareEl.querySelector('.visually-hidden');
            this.compareEl.textContent = compare;
            if (srOnly) this.compareEl.prepend(srOnly);
            this.compareEl.hidden = false;
          } else {
            this.compareEl.hidden = true;
          }
        }

        if (this.discountBadgeEl) {
          const savings = this.savingsBadgeText(metaPack.dataset.price, metaPack.dataset.compare);
          if (savings) {
            this.discountBadgeEl.textContent = savings;
            this.discountBadgeEl.hidden = false;
          } else {
            this.discountBadgeEl.hidden = true;
          }
        }
      }

      onVariantChange() {
        const variant = this.currentVariant;

        this.updateVariantIdInput(variant);
        this.updatePrice(variant);
        this.updateSubmitButton(variant);
        this.updateQuantityMax(variant);
        this.updateEffectiveQuantity(variant);
        this.updateMedia(variant);
        this.updateHistory(variant);
        this.updateOptionStates();
        this.dispatchVariantChange(variant);
      }

      /* ------------------------------------------------------------------ *
       * DOM updates
       * ------------------------------------------------------------------ */

      updateVariantIdInput(variant) {
        if (!this.variantIdInput) return;
        this.variantIdInput.value = variant ? variant.id : '';
        this.variantIdInput.disabled = !variant || !variant.available;
        this.variantIdInput.dispatchEvent(new Event('change', { bubbles: true }));
      }

      updatePrice(variant) {
        if (!this.priceEl) return;

        // Metaobject pack cards own the price display while one is selected.
        if (this.checkedPackMeta) {
          this.syncPackMetaDisplay();
          return;
        }

        if (!variant) {
          return;
        }

        this.priceEl.textContent = variant.price;

        if (this.compareEl) {
          if (variant.compare_at_price) {
            const srOnly = this.compareEl.querySelector('.visually-hidden');
            this.compareEl.textContent = variant.compare_at_price;
            if (srOnly) this.compareEl.prepend(srOnly);
            this.compareEl.hidden = false;
          } else {
            this.compareEl.hidden = true;
          }
        }

        if (this.discountBadgeEl) {
          const savings = this.savingsBadgeText(variant.price, variant.compare_at_price);
          if (savings) {
            this.discountBadgeEl.textContent = savings;
            this.discountBadgeEl.hidden = false;
          } else {
            this.discountBadgeEl.hidden = true;
          }
        }

        if (this.perGramEl && variant.per_gram) {
          const sep = this.perGramEl.querySelector('.v2-price__unit-sep');
          const label = this.perGramEl.querySelector('.v2-price__unit-label');
          this.perGramEl.textContent = variant.per_gram + ' ';
          if (sep) this.perGramEl.appendChild(sep);
          if (label) {
            this.perGramEl.appendChild(document.createTextNode(' '));
            this.perGramEl.appendChild(label);
          }
        }
      }

      updateSubmitButton(variant) {
        if (!this.submitButton) return;

        const strings = {
          addToCart: this.dataset.textAddToCart || 'Add to cart',
          soldOut: this.dataset.textSoldOut || 'Sold out',
          unavailable: this.dataset.textUnavailable || 'Unavailable',
        };

        let disabled = true;
        let text = strings.unavailable;

        if (variant) {
          if (variant.available) {
            disabled = false;
            text = strings.addToCart;
          } else {
            text = strings.soldOut;
          }
        }

        this.submitButton.disabled = disabled;
        if (this.submitButtonText) this.submitButtonText.textContent = text;

        // Disable the dynamic-checkout (Buy it now) button too when sold out.
        if (this.buyButtons) this.buyButtons.classList.toggle('is-soldout', disabled);
      }

      normalizeQuantityInput() {
        if (!this.qtyInput) return;
        const min = parseInt(this.qtyInput.min, 10) || 1;
        const max = parseInt(this.qtyInput.max, 10) || Infinity;
        let value = parseInt(this.qtyInput.value, 10);
        if (!Number.isFinite(value)) value = min;
        this.qtyInput.value = Math.min(Math.max(value, min), max);
      }

      /**
       * Caps the visible quantity stepper at the selected variant's available
       * stock (audit K02). Applies only when Shopify tracks inventory and the
       * variant does not allow overselling; otherwise any Liquid-rendered max
       * is cleared so switching variants never leaves a stale cap behind.
       */
      updateQuantityMax(variant) {
        if (!this.qtyInput) return;

        let max = null;
        if (
          variant &&
          variant.inventory_management === 'shopify' &&
          variant.inventory_policy !== 'continue' &&
          Number.isFinite(Number(variant.inventory_quantity))
        ) {
          max = Math.max(Number(variant.inventory_quantity), 1);
        }

        if (max === null) {
          this.qtyInput.removeAttribute('max');
        } else {
          this.qtyInput.max = max;
        }
        this.normalizeQuantityInput();
      }

      /**
       * Pack quantity logic. Two models depending on the layout:
       *   - Metaobject pack cards: the visible quantity box already holds the
       *     total number of units (it mirrors the selected pack's quantity, or
       *     a freely entered amount when no pack matches), so it is submitted
       *     as-is. Multiplying would double-count the pack quantity.
       *   - Variant packs / boxes: submitted quantity is
       *     (visible quantity, default 1) x (variant custom.pack_quantity, default 1).
       */
      updateEffectiveQuantity(variant = this.currentVariant) {
        if (!this.effectiveQtyInput) return;
        const visibleQty = this.qtyInput ? parseInt(this.qtyInput.value, 10) || 1 : 1;

        if (this.packMetaInputs.length) {
          this.effectiveQtyInput.value = Math.max(visibleQty, 1);
          return;
        }

        const packQty = this.packQuantityFor(variant);
        this.effectiveQtyInput.value = Math.max(visibleQty * packQty, 1);
      }

      updateMedia(variant) {
        if (!variant) return;
        const gallery = document.querySelector(`#MediaGallery-${this.sectionId}`) || this.querySelector('media-gallery');
        if (!gallery) return;

        if (variant.featured_media_id) {
          gallery.setActiveMedia?.(`${this.sectionId}-${variant.featured_media_id}`, true);
        }

        this.toggleVariantMedia(gallery, variant);
      }

      // Hides gallery/thumbnail items belonging to OTHER variants, only when the
      // section's "Hide other variants' media" setting is on (data-hide-variant-media).
      // Items with no data-variant-media attribute (shared, non-variant images) are
      // never touched here.
      toggleVariantMedia(gallery, variant) {
        if (gallery.dataset.hideVariantMedia !== 'true') return;

        const activeMediaId = variant.featured_media_id ? `${this.sectionId}-${variant.featured_media_id}` : null;
        gallery.querySelectorAll('[data-variant-media="true"]').forEach((item) => {
          const itemMediaId = item.dataset.mediaId || item.dataset.target;
          const isActive = activeMediaId !== null && itemMediaId === activeMediaId;
          item.classList.toggle('is-variant-hidden', !isActive);
        });
      }

      updateHistory(variant) {
        if (!this.shouldUpdateUrl || !variant || !this.productUrl) return;
        window.history.replaceState({}, '', `${this.productUrl}?variant=${variant.id}`);
      }

      /**
       * Layout 1 only: refresh availability styling and the price
       * sublabels shown inside the first option's boxes, based on the
       * currently selected values of the other options.
       */
      updateOptionStates() {
        const boxes = this.querySelector('[data-v2-variant-boxes]');
        if (!boxes) return;

        const selected = this.selectedOptionValues;

        boxes.querySelectorAll('[data-v2-option]').forEach((input) => {
          const position = Number(input.dataset.optionPosition);
          const candidateOptions = selected.slice();
          candidateOptions[position - 1] = input.value;

          const match = this.variants.find((variant) =>
            variant.options.every((option, index) => option === candidateOptions[index])
          );

          const label = boxes.querySelector(`label[for="${input.id}"]`);
          if (label) {
            label.classList.toggle('is-unavailable', !match || !match.available);
            const priceEl = label.querySelector('[data-v2-option-price]');
            if (priceEl && match) priceEl.textContent = match.price;
          }
        });
      }

      dispatchVariantChange(variant) {
        // Interop with the theme's pub/sub (cart drawer, apps, etc.).
        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.variantChange, {
            data: {
              sectionId: this.sectionId,
              variant,
            },
          });
        }

        this.dispatchEvent(
          new CustomEvent('product-info-v2:variant-change', {
            bubbles: true,
            detail: { sectionId: this.sectionId, variant },
          })
        );
      }

      /* ------------------------------------------------------------------ *
       * Wishlist (visual toggle + integration hook for wishlist apps)
       * ------------------------------------------------------------------ */

      initWishlist() {
        this.wishlistButton = this.querySelector('[data-v2-wishlist]');
        if (!this.wishlistButton) return;

        const storageKey = 'product-v2-wishlist';
        const handle = this.wishlistButton.dataset.productHandle;

        const readList = () => {
          try {
            return JSON.parse(window.localStorage.getItem(storageKey)) || [];
          } catch (error) {
            return [];
          }
        };

        const setPressed = (pressed) => {
          this.wishlistButton.setAttribute('aria-pressed', String(pressed));
          this.wishlistButton.classList.toggle('is-active', pressed);
        };

        setPressed(readList().includes(handle));

        this.wishlistButton.addEventListener('click', () => {
          let list = readList();
          const active = list.includes(handle);
          list = active ? list.filter((item) => item !== handle) : list.concat(handle);
          try {
            window.localStorage.setItem(storageKey, JSON.stringify(list));
          } catch (error) {
            /* storage unavailable — visual toggle only */
          }
          setPressed(!active);

          // Hook for wishlist apps.
          this.wishlistButton.dispatchEvent(
            new CustomEvent('product-v2:wishlist-toggle', {
              bubbles: true,
              detail: {
                handle,
                productId: this.wishlistButton.dataset.productId,
                added: !active,
              },
            })
          );
        });
      }
    }
  );
}
